import React, { useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './RangeSlider.styles.css';

const RangeSlider = ({ 
  min = 0,
  max = 100,
  step = 1,
  value,
  onChange,
}) => {
  const trackRef = useRef(null);
  const sliderRef = useRef(null);

  const calculateProgress = useCallback(() => {
    return ((value - min) / (max - min)) * 100;
  }, [value, min, max]);

  // Function to handle click on the track to set value
  const handleTrackClick = useCallback((e) => {
    e.stopPropagation();
    
    if (!trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    let clientX;
    
    // Handle both mouse and touch events
    if (e.type === 'touchstart' || e.type === 'touchmove') {
      // For touch events, use the first touch point
      clientX = e.touches[0].clientX;
    } else {
      // For mouse events
      clientX = e.clientX;
    }
    
    // Calculate relative position (0 to 1)
    const clickPosition = (clientX - rect.left) / rect.width;
    
    // Calculate the new value
    const newValue = min + clickPosition * (max - min);
    
    // Round to nearest step
    const steppedValue = Math.round(newValue / step) * step;
    
    // Ensure value is within bounds
    const boundedValue = Math.min(max, Math.max(min, steppedValue));
    
    // Update the value
    onChange(boundedValue);
    
    // Prevent event propagation to image
    e.preventDefault();
  }, [min, max, step, onChange]);

  // Add event listeners for thumb dragging
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    
    // Prevent touch events from propagating to parent elements
    const handleSliderInteraction = (e) => {
      e.stopPropagation();
    };
    
    slider.addEventListener('click', handleSliderInteraction);
    slider.addEventListener('touchstart', handleSliderInteraction, { passive: false });
    slider.addEventListener('touchmove', handleSliderInteraction, { passive: false });
    slider.addEventListener('touchend', handleSliderInteraction);
    
    return () => {
      slider.removeEventListener('click', handleSliderInteraction);
      slider.removeEventListener('touchstart', handleSliderInteraction);
      slider.removeEventListener('touchmove', handleSliderInteraction);
      slider.removeEventListener('touchend', handleSliderInteraction);
    };
  }, []);
  
  return (
    <div 
      className="range-slider"
      ref={sliderRef}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="range-slider-track-container" 
        ref={trackRef}
        onClick={handleTrackClick}
        onTouchStart={handleTrackClick}
      >
        <input
          type="range"
          className="range-slider-input"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          style={{ '--range-progress': `${calculateProgress()}%` }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

RangeSlider.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default RangeSlider; 